// Exemplos de uso da FloatingToolbar

import React from 'react';
import { 
  FloatingToolbar, 
  DemandToolbar, 
  UserToolbar, 
  AdminToolbar, 
  GeneralToolbar,
  createToolbarAction 
} from './index';
import { 
  HiTrash, 
  HiShare, 
  HiStar, 
  HiArrowDownTray, 
  HiEye,
  HiCog,
  HiUsers,
  HiChartBar
} from 'react-icons/hi2';

// 1. USO BÁSICO - Retrocompatibilidade
export const BasicExample = () => (
  <FloatingToolbar
    onEdit={() => console.log('Editar')}
    onReturn={() => console.log('Devolver')}
    relative={true}
  />
);

// 2. CONTEXTO DE DEMANDAS
export const DemandExample = () => (
  <DemandToolbar
    onEdit={() => console.log('Editar demanda')}
    onReturn={() => console.log('Devolver demanda')}
    onResolve={() => console.log('Solucionar demanda')}
    onAssignToSelf={() => console.log('Puxar para si')}
    onSendReminder={() => console.log('Enviar lembrete')}
    title="Demanda #123"
    position="bottom-right"
    size="md"
    relative={true}
  />
);

// 3. CONTEXTO DE USUÁRIO
export const UserExample = () => (
  <UserToolbar
    onEdit={() => console.log('Editar perfil')}
    title="Meu Perfil"
    position="top-right"
    size="sm"
    autoCollapseDelay={2000}
    relative={true}
  />
);

// 4. CONTEXTO ADMINISTRATIVO
export const AdminExample = () => (
  <AdminToolbar
    onEdit={() => console.log('Gerenciar sistema')}
    title="Admin"
    position="top-left"
    size="lg"
    scrollCollapse={false}
    relative={true}
  />
);

// 5. AÇÕES COMPLETAMENTE CUSTOMIZADAS
export const CustomActionsExample = () => {
  const customActions = [
    createToolbarAction(
      'view',
      'Visualizar',
      <HiEye className="w-4 h-4" />,
      () => console.log('Visualizar'),
      {
        color: 'text-blue-600',
        hoverColor: 'hover:bg-blue-50 hover:text-blue-700'
      }
    ),
    createToolbarAction(
      'download',
      'Baixar',
      <HiArrowDownTray className="w-4 h-4" />,
      () => console.log('Baixar'),
      {
        color: 'text-green-600',
        hoverColor: 'hover:bg-green-50 hover:text-green-700'
      }
    ),
    createToolbarAction(
      'share',
      'Compartilhar',
      <HiShare className="w-4 h-4" />,
      () => console.log('Compartilhar'),
      {
        color: 'text-purple-600',
        hoverColor: 'hover:bg-purple-50 hover:text-purple-700'
      }
    ),
    createToolbarAction(
      'favorite',
      'Favoritar',
      <HiStar className="w-4 h-4" />,
      () => console.log('Favoritar'),
      {
        color: 'text-yellow-600',
        hoverColor: 'hover:bg-yellow-50 hover:text-yellow-700'
      }
    ),
    createToolbarAction(
      'delete',
      'Deletar',
      <HiTrash className="w-4 h-4" />,
      () => console.log('Deletar'),
      {
        color: 'text-red-600',
        hoverColor: 'hover:bg-red-50 hover:text-red-700'
      }
    )
  ];

  return (
    <FloatingToolbar
      actions={customActions}
      title="Arquivo"
      position="bottom-left"
      size="md"
      autoCollapse={true}
      autoCollapseDelay={1500}
      closeOnAction={true}
      relative={true}
    />
  );
};

// 6. AÇÕES COM ESTADOS DINÂMICOS
export const DynamicActionsExample = () => {
  const [canEdit, setCanEdit] = React.useState(true);
  const [showFavorite, setShowFavorite] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);

  const dynamicActions = [
    createToolbarAction(
      'edit',
      'Editar',
      <HiCog className="w-4 h-4" />,
      () => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 2000);
      },
      {
        disabled: !canEdit || isLoading,
        color: 'text-blue-600'
      }
    ),
    createToolbarAction(
      'favorite',
      'Favoritar',
      <HiStar className="w-4 h-4" />,
      () => console.log('Favoritar'),
      {
        hidden: !showFavorite,
        color: 'text-yellow-600'
      }
    )
  ];

  return (
    <div>
      <div className="mb-4 space-x-2">
        <button 
          onClick={() => setCanEdit(!canEdit)}
          className="px-3 py-1 bg-blue-500 text-white rounded"
        >
          {canEdit ? 'Desabilitar Edição' : 'Habilitar Edição'}
        </button>
        <button 
          onClick={() => setShowFavorite(!showFavorite)}
          className="px-3 py-1 bg-yellow-500 text-white rounded"
        >
          {showFavorite ? 'Ocultar Favorito' : 'Mostrar Favorito'}
        </button>
      </div>
      
      <FloatingToolbar
        actions={dynamicActions}
        title="Dinâmico"
        position="bottom-right"
        relative={true}
      />
    </div>
  );
};

// 7. MÚLTIPLAS TOOLBARS (diferentes contextos)
export const MultipleToolbarsExample = () => (
  <div>
    {/* Toolbar principal de demandas */}
    <DemandToolbar
      onEdit={() => console.log('Editar demanda')}
      onResolve={() => console.log('Solucionar')}
      position="bottom-right"
      relative={true}
    />
    
    {/* Toolbar secundária de usuário */}
    <UserToolbar
      onEdit={() => console.log('Editar perfil')}
      position="top-right"
      size="sm"
      title="Perfil"
      relative={true}
    />
  </div>
);

// 8. TOOLBAR PARA DASHBOARD/RELATÓRIOS
export const DashboardToolbarExample = () => {
  const dashboardActions = [
    createToolbarAction(
      'users',
      'Usuários',
      <HiUsers className="w-4 h-4" />,
      () => console.log('Gerenciar usuários'),
      { color: 'text-blue-600' }
    ),
    createToolbarAction(
      'reports',
      'Relatórios',
      <HiChartBar className="w-4 h-4" />,
      () => console.log('Ver relatórios'),
      { color: 'text-green-600' }
    ),
    createToolbarAction(
      'settings',
      'Configurações',
      <HiCog className="w-4 h-4" />,
      () => console.log('Configurações'),
      { color: 'text-gray-600' }
    )
  ];

  return (
    <FloatingToolbar
      actions={dashboardActions}
      title="Dashboard"
      position="top-left"
      size="lg"
      autoCollapse={true}
      scrollCollapse={true}
      relative={true}
    />
  );
};

// 9. TOOLBAR MINIMALISTA
export const MinimalToolbarExample = () => (
  <GeneralToolbar
    onEdit={() => console.log('Ação única')}
    title="Ação"
    size="sm"
    position="bottom-left"
    autoCollapseDelay={500}
    relative={true}
  />
);

// 10. EXEMPLO COMPLETO COM TODAS AS CONFIGURAÇÕES
export const CompleteExample = () => {
  const completeActions = [
    createToolbarAction('action1', 'Ação 1', <HiEye className="w-4 h-4" />, () => {}),
    createToolbarAction('action2', 'Ação 2', <HiShare className="w-4 h-4" />, () => {}),
    createToolbarAction('action3', 'Ação 3', <HiArrowDownTray className="w-4 h-4" />, () => {}),
    createToolbarAction('action4', 'Ação 4', <HiStar className="w-4 h-4" />, () => {}),
    createToolbarAction('action5', 'Ação 5', <HiTrash className="w-4 h-4" />, () => {})
  ];

  return (
    <FloatingToolbar
      actions={completeActions}
      title="Exemplo Completo"
      position="bottom-right"
      size="md"
      autoCollapse={true}
      autoCollapseDelay={2000}
      scrollCollapse={true}
      closeOnAction={true}
      className="custom-toolbar"
      relative={true}
    />
  );
};

// 11. TOOLBAR DOCK HORIZONTAL
export const HorizontalDockExample = () => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  
  const dockActions = [
    createToolbarAction('home', 'Home', <HiEye className="w-4 h-4" />, () => console.log('Home')),
    createToolbarAction('search', 'Buscar', <HiShare className="w-4 h-4" />, () => console.log('Buscar')),
    createToolbarAction('download', 'Download', <HiArrowDownTray className="w-4 h-4" />, () => console.log('Download')),
    createToolbarAction('favorite', 'Favoritos', <HiStar className="w-4 h-4" />, () => console.log('Favoritos')),
    createToolbarAction('delete', 'Excluir', <HiTrash className="w-4 h-4" />, () => console.log('Excluir')),
    createToolbarAction('settings', 'Configurações', <HiCog className="w-4 h-4" />, () => console.log('Configurações'))
  ];

  return (
    <div 
      className="relative w-full h-full flex items-end justify-center pb-8"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Dock Container - Seguindo o padrão visual das outras toolbars */}
      <div 
        className="relative transition-all duration-500 ease-out"
        style={{
          borderRadius: '20px',
          boxShadow: "0 12px 48px rgba(0, 0, 0, 0.25), 0 4px 16px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1)"
        }}
      >
        {/* Glass Base Layer */}
        <div
          className="absolute inset-0 transition-all duration-300"
          style={{
            borderRadius: 'inherit',
            backdropFilter: "blur(10px)",
            background: "rgba(255, 255, 255, 0.1)"
          }}
        />
        
        {/* Main Glass Layer */}
        <div 
          className="absolute inset-0 transition-all duration-300"
          style={{
            borderRadius: 'inherit',
            background: `linear-gradient(135deg, 
              rgba(255,255,255,0.3) 0%, 
              rgba(255,255,255,0.15) 25%,
              rgba(255,255,255,0.05) 50%, 
              rgba(255,255,255,0.15) 75%,
              rgba(255,255,255,0.25) 100%)`,
            backdropFilter: "blur(2px)",
            border: "1px solid rgba(255,255,255,0.2)"
          }}
        />
        
        {/* Dock horizontal */}
        <div className={`
          relative flex items-center gap-1 p-2 transition-all duration-500 ease-out z-10
          ${isExpanded ? 'px-3' : 'px-2'}
        `}>
          {dockActions.map((action, index) => (
            <div
              key={action.id}
              className={`
                group relative flex items-center justify-center cursor-pointer
                transition-all duration-300 ease-out transform
                ${isExpanded 
                  ? 'w-10 h-10 hover:scale-110' 
                  : index === 0 
                    ? 'w-8 h-8' 
                    : 'w-0 h-0 opacity-0 overflow-hidden'
                }
              `}
              onClick={action.onClick}
              style={{
                transitionDelay: isExpanded ? `${index * 50}ms` : '0ms',
                borderRadius: '12px'
              }}
            >
              {/* Button Background */}
              <div 
                className={`
                  absolute inset-0 transition-all duration-300 opacity-0 group-hover:opacity-100
                  ${action.color === 'text-red-600' ? 'bg-red-500/20' : 'bg-white/20'}
                `}
                style={{ borderRadius: 'inherit' }}
              />
              
              {/* Icon */}
              {(isExpanded || index === 0) && (
                <div className={`
                  relative z-10 transition-all duration-200 ${action.color}
                  ${isExpanded ? 'opacity-100' : index === 0 ? 'opacity-80' : 'opacity-0'}
                `}>
                  {action.icon}
                </div>
              )}
              
              {/* Tooltip - seguindo o padrão das outras toolbars */}
              {isExpanded && (
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <div className="bg-black/80 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap backdrop-blur-sm">
                    {action.label}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Indicador de expansão */}
        {!isExpanded && (
          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white/60 rounded-full animate-pulse" />
        )}
      </div>
    </div>
  );
};

// 12. DEMO COMPLETA DA DEMAND TOOLBAR (todas as ações visíveis)
export const FullDemandToolbarExample = () => (
  <DemandToolbar
    // Exibir todas as opções com handlers padrão para demonstração
    onAssignToSelf={() => console.log('Puxar para mim')}
    onEdit={() => console.log('Editar demanda')}
    onAssignTo={() => console.log('Atribuir para')}
    onDeliver={() => console.log('Solucionar')}
    onResolve={() => console.log('Solucionar (retro)')}
    onAccept={() => console.log('Aceitar Demanda')}
    onUndoDelivery={() => console.log('Desfazer Entrega')}
    onAcceptDelivery={() => console.log('Aceitar Entrega')}
    onReturn={() => console.log('Devolver')}
    onReject={() => console.log('Recusar')}
    onDelete={() => console.log('Excluir demanda')}
    onAddObservers={() => console.log('Gerenciar Observadores')}
    onTransferSector={() => console.log('Transferir Setor')}
    onWrongDemand={() => console.log('Demanda Errada')}
    onresend={() => console.log('Reenviar')}
    onComment={() => console.log('Comentar')}
    onAttach={() => console.log('Anexar')}
    title="Demanda Demo (todas as ações)"
    position="bottom-right"
    size="md"
    relative={true}
  />
);

// Componente de demonstração com todos os exemplos
export const AllExamples = () => (
  <div className="p-8 space-y-8">
    <h1 className="text-2xl font-bold">FloatingToolbar - Exemplos de Uso</h1>
    
    <section>
      <h2 className="text-xl font-semibold mb-4">1. Uso Básico</h2>
      <BasicExample />
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-4">2. Contexto de Demandas</h2>
      <DemandExample />
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-4">3. Ações Customizadas</h2>
      <CustomActionsExample />
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-4">4. Estados Dinâmicos</h2>
      <DynamicActionsExample />
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-4">5. Dashboard</h2>
      <DashboardToolbarExample />
    </section>
  </div>
);
